import { AreaVersionSituationModelConstants } from '@/utils/constants/area-version-situation.model.constants'
import type { Email } from '@/views/administrative/users/types'

export const formatPhone = (phone: string): string => {
  const cleanNumber = phone.replace(/\D/g, '')

  if (cleanNumber.length === 10)
    return cleanNumber.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  else if (cleanNumber.length === 11)
    return cleanNumber.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')

  return phone
}

export const formatCpf = (cpf: string): string => {
  if (!cpf)
    return ''

  const cleanCpf = cpf.replace(/\D/g, '')

  if (cleanCpf.length !== 11)
    return cpf

  return cleanCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

export const formatRelativeTime = (value: string | Date): string => {
  const date = value instanceof Date ? value : new Date(value)
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

  if (seconds < 60)
    return 'Agora'

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60)
    return `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`

  const hours = Math.floor(minutes / 60)
  if (hours < 24)
    return `${hours} ${hours === 1 ? 'hora' : 'horas'}`

  const days = Math.floor(hours / 24)
  if (days < 7)
    return `${days} ${days === 1 ? 'dia' : 'dias'}`

  if (days < 30) {
    const weeks = Math.floor(days / 7)

    return `${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`
  }

  const months = Math.floor(days / 30)

  return `${months} ${months === 1 ? 'mês' : 'meses'}`
}

export const resolveUserProfileVariant = (profile: string) => {
  if (profile === 'Gestor')
    return { color: 'warning', icon: 'tabler-user' }
  if (profile === 'Leitor')
    return { color: 'success', icon: 'tabler-circle-check' }
  if (profile === 'Super-administrador')
    return { color: 'primary', icon: 'tabler-chart-pie-2' }
  if (profile === 'Administrador')
    return { color: 'info', icon: 'tabler-pencil' }
  if (profile === 'Editor')
    return { color: 'secondary', icon: 'tabler-server-2' }

  return { color: 'primary', icon: 'tabler-user' }
}

export const resolveUserStatusVariant = (status: boolean) => {
  return status ? 'success' : 'secondary'
}

export const corValidity = (date: string) => {
  const year = new Date(date).getFullYear()
  const currentYear = new Date().getFullYear()

  switch (true) {
  case year < currentYear:
    return 'error'
  case year > currentYear:
    return 'success'
  default:
    return 'warning'
  }
}

export const verifyEmails = (userEmails: Email[]) => {
  const emails
    = userEmails.length > 0
      ? userEmails
        .filter(value => {
          const id = Number(value.type.id)

          return id === 1 || id === 2 || id === 3
        })
        .sort((a, b) => Number(a.type.id) - Number(b.type.id))
      : []

  return emails.length > 0 ? emails[0].email : null
}

export const capitalizeOnlyFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const capitalizeOnlyFirstLetterWords = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      if (index === 0)
        return word.charAt(0).toUpperCase() + word.slice(1)

      return word
    })
    .join(' ')
}

export const capitalizeWords = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

export const capitalizeWordsWithPrepositions = (str: string) => {
  const prepositions = new Set([
    'de',
    'da',
    'dos',
    'do',
    'das',
    'e',
    'a',
    'as',
    'o',
    'os',
    'para',
    'na',
    'nas',
    'no',
    'nos',
    'em',
    'com',
    'por',
  ])

  return str
    .toLowerCase()
    .split(' ')
    .map((word, index, array) => {
      if (
        index === 0
        || index === array.length - 1
        || !prepositions.has(word)
      )
        return word.charAt(0).toUpperCase() + word.slice(1)

      return word
    })
    .join(' ')
}

export const capitalizeWordsWithPrepositionsAndRomanNumerals = (
  str: string,
) => {
  const prepositions = new Set([
    'de',
    'da',
    'dos',
    'do',
    'das',
    'e',
    'a',
    'as',
    'o',
    'os',
    'para',
    'na',
    'nas',
    'no',
    'nos',
    'em',
    'com',
    'por',
  ])

  const romanNumerals = new Set([
    'i',
    'ii',
    'iii',
    'iv',
    'v',
    'vi',
    'vii',
    'viii',
    'ix',
    'x',
  ])

  return str
    .toLowerCase()
    .split(' ')
    .map((word, index, array) => {
      if (romanNumerals.has(word))
        return word.toUpperCase()

      if (
        index === 0
        || index === array.length - 1
        || !prepositions.has(word)
      )
        return word.charAt(0).toUpperCase() + word.slice(1)

      return word
    })
    .join(' ')
}

export const resolveAreaSituationVariant = (situationId: string) => {
  switch (situationId) {
  case AreaVersionSituationModelConstants.APPROVED_COMPLETED.ID:
    return 'success'
  case AreaVersionSituationModelConstants.IN_ANALYSIS.ID:
    return 'default'
  case AreaVersionSituationModelConstants.AWAITING_APPROVAL.ID:
    return 'warning'
  case AreaVersionSituationModelConstants.DECLINED.ID:
    return 'error'
  default:
    return 'primary'
  }
}

export function groupBy<T, K extends PropertyKey>(
  items: T[],
  callback: (item: T) => K,
): Record<K, T[]> {
  return items.reduce(
    (acc, item) => {
      const key = callback(item)

      ;(acc[key] ||= []).push(item) // se não existe cria array e já insere

      return acc
    },
    {} as Record<K, T[]>,
  )
}

export const colorSimilarity = (score: number) => {
  if (score < 31)
    return 'error'
  if (score < 69)
    return 'warning'

  return 'success'
}

export const formatPercent = (value: string) => {
  const number = Number.parseFloat(value.trim())

  return `${number.toFixed(2).replace('.', ',')}%`
}

const INVARIABLE_WORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em'])

const pluralizeWord = (word: string): string => {
  if (INVARIABLE_WORDS.has(word.toLowerCase()))
    return word

  if (word.endsWith('ão'))
    return `${word.slice(0, -2)}ões`

  if (/[aeiou]l$/i.test(word))
    return `${word.slice(0, -1)}is`

  if (word.endsWith('m'))
    return `${word.slice(0, -1)}ns`

  if (/[rz]$/i.test(word))
    return `${word}es`

  if (word.endsWith('s'))
    return word

  return `${word}s`
}

export const pluralizePtBr = (phrase: string): string =>
  phrase.split(' ').map(pluralizeWord).join(' ')

export const pluralizeByCount = (count: number, phrase: string): string =>
  count === 1 ? phrase : pluralizePtBr(phrase)
